import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { subMonths, endOfMonth } from 'date-fns'

// Initial state
const initialState = {
  data: {
    customers: [],
    months: [],
    totals: [],
    grandTotal: null
  },
  allCustomers: [], // All available customers for filtering
  filters: {
    dateRange: {
      from: subMonths(new Date(), 12), // 12 months ago from today
      to: endOfMonth(subMonths(new Date(), 1)) // End of last month (exclude current month)
    },
    selectedCustomers: [],
    projectQuery: '',
    thresholds: {
      gpm_percent: { target: 25, warning: 20 },
      gpm_dollar: { target: 10000, warning: 5000 },
      revenue: { target: 50000, warning: 25000 },
      delivery_cost: { target: 40000, warning: 60000 }, // Inverse
      hours_spent: { target: 1000, warning: 1500 },     // Inverse
      hours_billed: { target: 800, warning: 500 }
    }
  },
  ui: {
    expandedCustomer: null,
    loading: false,
    error: null,
    sortColumn: null,
    sortDirection: 'asc',
    visibleMetrics: ['gpm_percent', 'revenue'], // DEFAULT: GPM % + Revenue (for customer rows)
    projectVisibleMetrics: ['delivery_cost', 'hours_spent'], // DEFAULT: Delivery Cost + Hours Spent (for project rows)
    // Design 33a. The desktop grid shows ONE metric at a time, which is a
    // different control from the phone's multi-select chips — so it gets its
    // own field rather than narrowing `visibleMetrics` to a single entry.
    // `MarginMobileList`, `MarginMobileDetail` (via `resolveMetrics`) and
    // `ExportDialog` all read `visibleMetrics`; collapsing it would leave the
    // phone rows with no secondary line and the export with one column.
    gridMetric: 'gpm_percent',
    // 'worst' | 'best' | 'name'. 33a sorts worst-first so the tinted cells
    // cluster at the top instead of being hunted for.
    sortMode: 'worst'
  }
}

// Thunk: Fetch margin data from API
export const fetchMarginData = createAsyncThunk(
  'marginAnalysis/fetchMarginData',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().marginAnalysis
      const { dateRange, selectedCustomers, projectQuery } = filters

      // Format dates for API
      const fromDate = dateRange.from.toISOString().split('T')[0]
      const toDate = dateRange.to.toISOString().split('T')[0]

      // Build query params
      const params = {
        'dateRange.from': fromDate,
        'dateRange.to': toDate
      }

      // Add optional filters (customer filter is done client-side)
      if (projectQuery && projectQuery.trim()) {
        params.projectFilter = projectQuery.trim()
      }

      const response = await axios.get('/v1/margin-analysis/matrix', { params })

      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch margin data')
    }
  }
)

// Thunk: Fetch all customers for filter dropdown
export const fetchAllCustomers = createAsyncThunk(
  'marginAnalysis/fetchAllCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/customers')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customers')
    }
  }
)

// Thunk: Update visible metrics (and optionally save to user preferences)
export const updateVisibleMetrics = createAsyncThunk(
  'marginAnalysis/updateVisibleMetrics',
  async (metrics, { rejectWithValue }) => {
    try {
      // TODO: Save to user preferences endpoint when available
      // await axios.put('/api/v1/users/preferences', { marginAnalysisMetrics: metrics })

      return metrics
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save preferences')
    }
  }
)

// Thunk: Update thresholds
export const updateThresholds = createAsyncThunk(
  'marginAnalysis/updateThresholds',
  async (thresholds, { rejectWithValue }) => {
    try {
      // TODO: Save to user preferences when Story 4.5 is implemented
      return thresholds
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update thresholds')
    }
  }
)

// Slice
const marginAnalysisSlice = createSlice({
  name: 'marginAnalysis',
  initialState,
  reducers: {
    // Set date range filter
    setDateRange: (state, action) => {
      state.filters.dateRange = action.payload
    },
    // Set selected customers filter
    setSelectedCustomers: (state, action) => {
      state.filters.selectedCustomers = action.payload
    },
    // Set project query filter
    setProjectQuery: (state, action) => {
      state.filters.projectQuery = action.payload
    },
    // Toggle expanded customer (accordion behavior - only one expanded at a time)
    toggleExpandedCustomer: (state, action) => {
      const customerId = action.payload
      if (state.ui.expandedCustomer === customerId) {
        // Clicking same customer collapses it
        state.ui.expandedCustomer = null
      } else {
        // Clicking different customer expands it (auto-collapses previous)
        state.ui.expandedCustomer = customerId
      }
    },
    // Set sort column and direction
    setSort: (state, action) => {
      const { column, direction } = action.payload
      state.ui.sortColumn = column
      state.ui.sortDirection = direction
    },
    // Set the single metric the desktop grid renders (design 33a)
    setGridMetric: (state, action) => {
      state.ui.gridMetric = action.payload
    },
    // Set the desktop grid's row order (design 33a)
    setSortMode: (state, action) => {
      state.ui.sortMode = action.payload
    },
    // Clear error
    clearError: (state) => {
      state.ui.error = null
    }
  },
  extraReducers: (builder) => {
    // fetchMarginData
    builder
      .addCase(fetchMarginData.pending, (state) => {
        state.ui.loading = true
        state.ui.error = null
        state.ui.expandedCustomer = null // Reset expanded state on data reload
      })
      .addCase(fetchMarginData.fulfilled, (state, action) => {
        state.ui.loading = false
        state.data = action.payload
      })
      .addCase(fetchMarginData.rejected, (state, action) => {
        state.ui.loading = false
        state.ui.error = action.payload
      })

    // fetchAllCustomers
    builder
      .addCase(fetchAllCustomers.fulfilled, (state, action) => {
        state.allCustomers = action.payload
      })
      .addCase(fetchAllCustomers.rejected, (state, action) => {
        console.error('Failed to fetch all customers:', action.payload)
      })

    // updateVisibleMetrics
    builder
      .addCase(updateVisibleMetrics.fulfilled, (state, action) => {
        state.ui.visibleMetrics = action.payload
        state.ui.expandedCustomer = null // Reset expanded state when metrics change
      })
      .addCase(updateVisibleMetrics.rejected, (state, action) => {
        // If save fails, still update locally
        console.error('Failed to save metric preferences:', action.payload)
      })

    // updateThresholds
    builder
      .addCase(updateThresholds.fulfilled, (state, action) => {
        state.filters.thresholds = action.payload
      })
      .addCase(updateThresholds.rejected, (state, action) => {
        console.error('Failed to update thresholds:', action.payload)
      })
  }
})

// Actions
export const {
  setDateRange,
  setSelectedCustomers,
  setProjectQuery,
  toggleExpandedCustomer,
  setSort,
  setGridMetric,
  setSortMode,
  clearError
} = marginAnalysisSlice.actions

// Selectors
export const selectMarginAnalysis = (state) => state.marginAnalysis
export const selectCustomers = (state) => state.marginAnalysis.data.customers
export const selectAllCustomers = (state) => state.marginAnalysis.allCustomers
export const selectMonths = (state) => state.marginAnalysis.data.months
export const selectTotals = (state) => state.marginAnalysis.data.totals
export const selectGrandTotal = (state) => state.marginAnalysis.data.grandTotal
export const selectVisibleMetrics = (state) => state.marginAnalysis.ui.visibleMetrics
export const selectProjectVisibleMetrics = (state) => state.marginAnalysis.ui.projectVisibleMetrics
export const selectGridMetric = (state) => state.marginAnalysis.ui.gridMetric
export const selectSortMode = (state) => state.marginAnalysis.ui.sortMode
export const selectLoading = (state) => state.marginAnalysis.ui.loading
export const selectError = (state) => state.marginAnalysis.ui.error
export const selectThresholds = (state) => state.marginAnalysis.filters.thresholds
export const selectExpandedCustomer = (state) => state.marginAnalysis.ui.expandedCustomer
export const selectFilters = (state) => state.marginAnalysis.filters
export const selectDateRange = (state) => state.marginAnalysis.filters.dateRange
export const selectProjectQuery = (state) => state.marginAnalysis.filters.projectQuery

// Filtered customers selector (client-side filtering)
export const selectFilteredCustomers = (state) => {
  const customers = state.marginAnalysis.data.customers
  const selectedCustomerIds = state.marginAnalysis.filters.selectedCustomers

  // If no customers are selected, return all customers
  if (!selectedCustomerIds || selectedCustomerIds.length === 0) {
    return customers
  }

  // Filter customers based on selected IDs
  return customers.filter(customer => selectedCustomerIds.includes(customer.id))
}

// Filtered totals selector (recalculates totals for filtered customers)
export const selectFilteredTotals = (state) => {
  const filteredCustomers = selectFilteredCustomers(state)
  const totals = state.marginAnalysis.data.totals
  const selectedCustomerIds = state.marginAnalysis.filters.selectedCustomers

  // If no customers are selected, return original totals
  if (!selectedCustomerIds || selectedCustomerIds.length === 0) {
    return totals
  }

  // If no customers match filter, return empty totals
  if (!filteredCustomers || filteredCustomers.length === 0) {
    return []
  }

  // Recalculate totals by summing up the filtered customers' data
  return totals.map((monthTotal, index) => {
    // Field names must match `customer.margins[]`, which is what
    // `getMetricValue` maps onto (marginFormatters.js): gpm / gpmDollar /
    // revenue / deliveryCost / spentHours / billedHours. This used to both
    // emit and read snake_case, so with a customer filter applied every
    // metric but revenue resolved to 0 — the totals row read 0.0% GPM, and
    // the summing loop's `margin.delivery_cost` was always undefined, which
    // made gpmDollar equal revenue and gpm a flat 100%.
    const recalculatedTotal = {
      month: monthTotal.month,
      gpm: 0,
      gpmDollar: 0,
      revenue: 0,
      deliveryCost: 0,
      spentHours: 0,
      billedHours: 0
    }

    // Sum up values from filtered customers for this month
    filteredCustomers.forEach(customer => {
      if (customer.margins && customer.margins[index]) {
        const margin = customer.margins[index]
        recalculatedTotal.revenue += margin.revenue || 0
        recalculatedTotal.deliveryCost += margin.deliveryCost || 0
        recalculatedTotal.spentHours += margin.spentHours || 0
        recalculatedTotal.billedHours += margin.billedHours || 0
      }
    })

    // Calculate GPM after summing revenue and delivery cost
    recalculatedTotal.gpmDollar = recalculatedTotal.revenue - recalculatedTotal.deliveryCost
    recalculatedTotal.gpm = recalculatedTotal.revenue > 0
      ? (recalculatedTotal.gpmDollar / recalculatedTotal.revenue) * 100
      : 0

    return recalculatedTotal
  })
}

// Filtered grand total selector (recalculates grand total for filtered customers)
export const selectFilteredGrandTotal = (state) => {
  const filteredTotals = selectFilteredTotals(state)
  const selectedCustomerIds = state.marginAnalysis.filters.selectedCustomers

  // If no customers are selected, return original grand total
  if (!selectedCustomerIds || selectedCustomerIds.length === 0) {
    return state.marginAnalysis.data.grandTotal
  }

  // If no totals, return null
  if (!filteredTotals || filteredTotals.length === 0) {
    return null
  }

  // Calculate grand total by summing up all filtered monthly totals
  // Same field names as `customer.margins[]` — see the note in
  // selectFilteredTotals. This had the identical snake_case mismatch.
  const grandTotal = {
    gpm: 0,
    gpmDollar: 0,
    revenue: 0,
    deliveryCost: 0,
    spentHours: 0,
    billedHours: 0
  }

  filteredTotals.forEach(monthTotal => {
    grandTotal.revenue += monthTotal.revenue || 0
    grandTotal.deliveryCost += monthTotal.deliveryCost || 0
    grandTotal.spentHours += monthTotal.spentHours || 0
    grandTotal.billedHours += monthTotal.billedHours || 0
  })

  // Calculate GPM for grand total
  grandTotal.gpmDollar = grandTotal.revenue - grandTotal.deliveryCost
  grandTotal.gpm = grandTotal.revenue > 0
    ? (grandTotal.gpmDollar / grandTotal.revenue) * 100
    : 0

  return grandTotal
}

// Reducer
export default marginAnalysisSlice.reducer
